import React, { useState } from 'react';

const DecisionWithRegimenStep = ({ question, value, onAnswer, onNext, answers }) => {
  const [selectedOption, setSelectedOption] = useState(value?.decision || '');
  const [selectedRegimen, setSelectedRegimen] = useState(value?.regimen || '');

  // Determine medication type from previous answers
  const getMedicationType = () => {
    const recentDose = answers?.['recent-dose'] || answers?.['current-dose'];
    
    if (recentDose) {
      const doseLower = recentDose.toLowerCase();
      if (doseLower.includes('tirzepatide')) return 'tirzepatide';
      if (doseLower.includes('semaglutide') || doseLower.includes('ozempic') || doseLower.includes('wegovy')) return 'semaglutide';
    }
    
    const currentMedication = answers?.['current-glp-medications'];
    if (Array.isArray(currentMedication)) {
      if (currentMedication.includes('tirzepatide')) return 'tirzepatide';
      if (currentMedication.includes('semaglutide')) return 'semaglutide';
    }
    
    return 'semaglutide';
  };

  const medicationType = getMedicationType();
  const regimens = medicationType === 'tirzepatide' ? question.tirzepatideRegimens : question.semaglutideRegimens;

  const handleOptionSelect = (optionValue) => {
    setSelectedOption(optionValue);
    setSelectedRegimen(''); // Reset regimen when option changes
    
    // Find the selected option to get regimen type
    const option = question.options.find(o => o.value === optionValue);
    onAnswer({ decision: optionValue, regimen: '', regimenType: option?.showRegimen });
  };

  const handleRegimenSelect = (e) => {
    const regimenValue = e.target.value;
    setSelectedRegimen(regimenValue);
    
    const option = question.options.find(o => o.value === selectedOption);
    onAnswer({ decision: selectedOption, regimen: regimenValue, regimenType: option?.showRegimen });
  };

  const selectedOptionData = question.options.find(o => o.value === selectedOption);
  const regimenOptions = selectedOptionData ? regimens[selectedOptionData.showRegimen] : [];

  const canContinue = selectedOption && selectedRegimen;

  return (
    <div data-testid={`step-${question.id}`}>
      <h2 className="text-2xl md:text-3xl font-semibold text-center text-gray-900 mb-8">
        {question.question}
      </h2>

      <div className="space-y-4 mb-6">
        {question.options.map((option, index) => {
          const isSelected = selectedOption === option.value;
          const optionRegimens = regimens[option.showRegimen] || [];
          
          return (
            <div key={index} className="space-y-3">
              {/* Option button */}
              <button
                onClick={() => handleOptionSelect(option.value)}
                className={`w-full p-5 rounded-xl text-left transition-all border-2 ${
                  isSelected 
                    ? 'border-[#2d8a5f] bg-[#e8f5ee]' 
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
                data-testid={`option-${index}`}
              >
                <div className="flex items-center gap-4">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                    isSelected ? 'bg-[#2d8a5f] border-[#2d8a5f]' : 'border-gray-300'
                  }`}>
                    {isSelected && (
                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                  <span className="text-base font-medium text-gray-800">{option.label}</span>
                </div>
              </button>

              {/* Regimen dropdown - shown when option is selected */}
              {isSelected && (
                <div className="ml-9 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <h4 className="text-lg font-semibold text-gray-800 mb-2">
                    {medicationType === 'tirzepatide' ? 'Tirzepatide' : 'Semaglutide'}: {option.regimenTitle}
                  </h4>
                  <p className="text-sm text-gray-600 mb-3">
                    {option.regimenSubtitle}
                  </p>
                  
                  {option.regimenNote && (
                    <p className="text-sm text-amber-700 bg-amber-50 p-2 rounded mb-3">
                      {option.regimenNote}
                    </p>
                  )}

                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Possible regimen the doctor may prescribe:
                  </label>
                  <select
                    value={selectedRegimen}
                    onChange={handleRegimenSelect}
                    className="form-input w-full"
                    data-testid="regimen-select"
                  >
                    <option value="">-- Select a regimen option --</option>
                    {optionRegimens.map((regimen, rIndex) => (
                      <option key={rIndex} value={regimen.value}>
                        {regimen.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <button
        onClick={onNext}
        disabled={!canContinue}
        className="btn-primary"
        data-testid="continue-button"
      >
        Continue
      </button>
    </div>
  );
};

export default DecisionWithRegimenStep;
